import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import {
  Users, UsersRound, Calendar, KeySquare, Plus, Search, Edit3, Trash2, X, UserPlus,
  ClipboardCheck, ChevronRight, BookUser, QrCode, Copy, Printer, BarChart3, ArrowUpDown, Home,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { DirectoryPanel } from "@/pages/CoordinatorDashboard";
import HouseholdsPanel from "@/components/crm/HouseholdsPanel";
import ChurchToolsSwitcher from "@/components/ChurchToolsSwitcher";
import GenderField from "@/components/GenderField";
import { formatDisplayName } from "@/lib/names";
import AddDirectoryPersonDialog from "@/components/crm/AddDirectoryPersonDialog";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const AGE_GROUPS = ["Child", "Youth", "Young Adult", "Adult", "Senior"];

function TabButton({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex shrink-0 items-center gap-2 px-3 sm:px-4 py-2 text-sm rounded-full transition-colors" style={{ background: active ? "var(--ink)" : "transparent", color: active ? "var(--bg-base)" : "var(--ink-2)", fontWeight: active ? 600 : 400 }}>
      <Icon size={14} /> {label}
      {typeof count === "number" && (
        <span className="text-[0.6rem] px-1.5 py-0.5 rounded-full" style={{ background: active ? "rgba(255,255,255,0.2)" : "var(--surface-hover)", color: active ? "#FFFFFF" : "var(--ink-2)" }}>{count}</span>
      )}
    </button>
  );
}

export default function AttendanceDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("disciples");
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [disciples, setDisciples] = useState([]);
  const [sections, setSections] = useState([]);
  const [services, setServices] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading || !user) return;
    if (user.is_admin) {
      api.get("/admin/orgs").then((r) => {
        const list = r.data || [];
        setOrgs(list);
        setOrgId((current) => current || list[0]?.id || null);
      });
    } else {
      setOrgId(user.org_id);
    }
  }, [user, loading]);

  const load = () => {
    if (!orgId) return;
    setBusy(true);
    const params = { org_id: orgId };
    Promise.all([
      api.get("/att/disciples", { params }),
      api.get("/att/sections", { params }),
      api.get("/att/services", { params }),
      api.get("/att/leaders", { params }),
    ]).then(([d, s, sv, l]) => {
      setDisciples(d.data || []);
      setSections(s.data || []);
      setServices(sv.data || []);
      setLeaders(l.data || []);
    }).finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  if (loading) return null;
  if (!user) return <Navigate to="/attendance/login" replace />;
  if (!user.is_admin && !user.is_coordinator) return <Navigate to="/attendance/section" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10" data-testid="attendance-dashboard">
      <div className="mb-6">
        <ChurchToolsSwitcher />
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Church body attendance</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight mt-1 break-words" style={{ color: "var(--ink)" }}>
            thedisciple<span style={{ color: "var(--terracotta)" }}>.attendance</span>
          </h1>
          <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--ink-2)" }}>
            Manage your church's master disciple list, split them into sections, assign leaders, and configure the recurring services you'll take attendance for.
          </p>
        </div>
        {user.is_admin && orgs.length > 1 && (
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Organization</label>
            <select value={orgId || ""} onChange={(e) => setOrgId(e.target.value)} className="px-3 py-2 text-sm" data-testid="att-org-select">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-2 mb-8 p-1 rounded-full max-w-full overflow-x-auto" style={{ background: "var(--surface-hover)", width: "fit-content" }}>
        <TabButton active={tab === "disciples"} onClick={() => setTab("disciples")} icon={Users} label="Disciples" count={disciples.length} testid="att-tab-disciples" />
        <TabButton active={tab === "directory"} onClick={() => setTab("directory")} icon={BookUser} label="Directory" testid="att-tab-directory" />
        <TabButton active={tab === "households"} onClick={() => setTab("households")} icon={Home} label="Households" testid="att-tab-households" />
        <TabButton active={tab === "sections"} onClick={() => setTab("sections")} icon={UsersRound} label="Sections" count={sections.length} testid="att-tab-sections" />
        <TabButton active={tab === "services"} onClick={() => setTab("services")} icon={Calendar} label="Services" count={services.length} testid="att-tab-services" />
        <TabButton active={tab === "leaders"} onClick={() => setTab("leaders")} icon={KeySquare} label="Section leaders" count={leaders.length} testid="att-tab-leaders" />
        <TabButton active={tab === "reports"} onClick={() => setTab("reports")} icon={BarChart3} label="Reports" testid="att-tab-reports" />
      </div>

      {busy ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : (
        <>
          {tab === "disciples" && <DisciplesPanel orgId={orgId} disciples={disciples} sections={sections} onReload={load} />}
          {tab === "directory" && <DirectoryPanel orgId={orgId} />}
          {tab === "households" && <HouseholdsPanel orgId={orgId} />}
          {tab === "sections" && <SectionsPanel orgId={orgId} sections={sections} disciples={disciples} leaders={leaders} onReload={load} />}
          {tab === "services" && <ServicesPanel orgId={orgId} services={services} onReload={load} />}
          {tab === "leaders" && <LeadersPanel orgId={orgId} leaders={leaders} sections={sections} onReload={load} />}
          {tab === "reports" && <ReportsPanel orgId={orgId} sections={sections} />}
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------------- DISCIPLES ---
function DisciplesPanel({ orgId, disciples, sections, onReload }) {
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // null | {kind:"new"|"edit", disciple?}
  const [confirmDelete, setConfirmDelete] = useState(null); // disciple or null

  const filtered = useMemo(() => {
    const l = q.trim().toLowerCase();
    if (!l) return disciples;
    return disciples.filter((d) =>
      `${d.first_name} ${d.last_name}`.toLowerCase().includes(l) ||
      (d.phone || "").includes(l) ||
      (d.email || "").toLowerCase().includes(l) ||
      (d.address_city || "").toLowerCase().includes(l)
    );
  }, [disciples, q]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/att/disciples/${confirmDelete.id}`, { params: { org_id: orgId } });
      toast.success("Disciple removed.");
      setConfirmDelete(null);
      onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete."); }
  };

  const sectionName = (id) => sections.find((s) => s.id === id)?.name || "—";

  return (
    <section data-testid="att-disciples-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[16rem] max-w-lg" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)", borderRadius: "999px", padding: "0.4rem 0.9rem" }}>
          <Search size={14} style={{ color: "var(--muted)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, city…" className="flex-1 bg-transparent outline-none text-sm" data-testid="att-disciple-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setModal({ kind: "promote" })} className="btn-primary" data-testid="att-add-from-tracker-btn" title="Add someone from the church directory">
            <UserPlus size={14} /> Add from directory
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title={disciples.length === 0 ? "No disciples yet" : "No matches"} description={disciples.length === 0 ? "Add your first church-body disciple. When someone graduates from First Steps + Next Steps, promote them here for weekly attendance tracking." : "Try a different search."} action={disciples.length === 0 && <button onClick={() => setModal({ kind: "promote" })} className="btn-primary"><UserPlus size={14} /> Add from directory</button>} testid="att-disciples-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="att-disciples-grid">
          {filtered.map((d) => (
            <div key={d.id} className="card-surface p-5 flex flex-col" data-testid={`att-disciple-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-medium text-lg" style={{ color: "var(--ink)" }}>{formatDisplayName(d)}</div>
                  <div className="text-[0.65rem] uppercase tracking-[0.14em] mt-0.5" style={{ color: "var(--gold)" }}>{sectionName(d.section_id)}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setModal({ kind: "edit", disciple: d })} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-edit-disciple-${d.id}`} title="Edit">
                    <Edit3 size={13} style={{ color: "var(--ink-2)" }} />
                  </button>
                  <button onClick={() => setConfirmDelete(d)} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-delete-disciple-${d.id}`} title="Remove">
                    <Trash2 size={13} style={{ color: "var(--terracotta)" }} />
                  </button>
                </div>
              </div>
              <div className="space-y-1 text-xs" style={{ color: "var(--ink-2)" }}>
                {d.phone && <div>{d.phone}</div>}
                {d.email && <div>{d.email}</div>}
                {d.address_city && <div style={{ color: "var(--muted)" }}>{d.address_city}</div>}
                {d.age_group && <div className="inline-block px-1.5 py-0.5 text-[0.6rem] rounded-full mt-1" style={{ background: "var(--surface-hover)" }}>{d.age_group}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal?.kind === "promote" && (
        <PromoteFromTrackerModal orgId={orgId} sections={sections} onClose={() => setModal(null)} onSaved={onReload} />
      )}
      {modal && modal.kind !== "promote" && <DiscipleModal orgId={orgId} sections={sections} disciple={modal.disciple} onClose={() => setModal(null)} onSaved={onReload} />}
      {confirmDelete && (
        <ConfirmDialog
          title={`Remove ${formatDisplayName(confirmDelete)}?`}
          message="This will remove them from the master list and any section they were assigned to. This cannot be undone."
          onCancel={() => setConfirmDelete(null)}
          onConfirm={doDelete}
          confirmLabel="Remove disciple"
        />
      )}
    </section>
  );
}

function DiscipleModal({ orgId, sections, disciple, onClose, onSaved }) {
  const isEdit = !!disciple;
  const [form, setForm] = useState({
    first_name: disciple?.first_name || "",
    last_name: disciple?.last_name || "",
    gender: disciple?.gender || null,
    phone: disciple?.phone || "",
    email: disciple?.email || "",
    address_city: disciple?.address_city || "",
    age_group: disciple?.age_group || "",
    referral_source: disciple?.referral_source || "",
    invited_by: disciple?.invited_by || "",
    notes: disciple?.notes || "",
    section_id: disciple?.section_id || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() && !form.last_name.trim()) {
      toast.error("First or last name is required."); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, section_id: form.section_id || null };
      if (isEdit) {
        await api.put(`/att/disciples/${disciple.id}`, payload, { params: { org_id: orgId } });
      } else {
        await api.post("/att/disciples", payload, { params: { org_id: orgId } });
      }
      toast.success(isEdit ? "Saved." : "Disciple added.");
      onSaved(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-xl card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="att-disciple-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-4" style={{ color: "var(--ink)" }}>{isEdit ? "Edit disciple" : "Add disciple"}</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name *"><input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="att-disciple-first_name" className="w-full px-3 py-2 text-sm" /></Field>
          <Field label="Last name *"><input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="att-disciple-last_name" className="w-full px-3 py-2 text-sm" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="att-disciple-phone" className="w-full px-3 py-2 text-sm" /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="att-disciple-email" className="w-full px-3 py-2 text-sm" /></Field>
          <Field label="City"><input value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} data-testid="att-disciple-city" className="w-full px-3 py-2 text-sm" /></Field>
          <Field label="Age group">
            <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} data-testid="att-disciple-age" className="w-full px-3 py-2 text-sm">
              <option value="">—</option>
              {AGE_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Referral source"><input value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })} data-testid="att-disciple-referral" className="w-full px-3 py-2 text-sm" /></Field>
          <Field label="Invited by"><input value={form.invited_by} onChange={(e) => setForm({ ...form, invited_by: e.target.value })} data-testid="att-disciple-invited_by" className="w-full px-3 py-2 text-sm" /></Field>
          <Field label="Section" span2>
            <select value={form.section_id} onChange={(e) => setForm({ ...form, section_id: e.target.value })} data-testid="att-disciple-section" className="w-full px-3 py-2 text-sm">
              <option value="">— Unassigned —</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Gender" span2>
            <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="att-disciple-gender" label={null} />
          </Field>
          <Field label="Notes" span2>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="att-disciple-notes" className="w-full px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="mt-6 flex gap-2">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="att-disciple-submit">{busy ? "Saving…" : (isEdit ? "Save changes" : "Add disciple")}</button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

const STATUS_META = {
  guest:    { label: "Guest",    tint: "var(--muted)" },
  disciple: { label: "Disciple", tint: "var(--orange)" },
  member:   { label: "Member",   tint: "var(--gold)" },
};

function PromoteFromTrackerModal({ orgId, sections, onClose, onSaved }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sectionId, setSectionId] = useState("");
  const [promoting, setPromoting] = useState(null);
  const [adding, setAdding] = useState(false);

  const search = async (qStr) => {
    setLoading(true);
    try {
      const r = await api.get("/att/promote-candidates", { params: { org_id: orgId, q: qStr } });
      setResults(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { search(""); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [query]);

  const promote = async (person) => {
    setPromoting(person.id);
    try {
      await api.post("/att/promote-guest", { guest_id: person.id, section_id: sectionId || null }, { params: { org_id: orgId } });
      toast.success(`${formatDisplayName(person)} added to attendance.`);
      onSaved?.();
      // remove from results so the coord can promote several people in a row
      setResults((prev) => prev.filter((p) => p.id !== person.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add.");
    } finally {
      setPromoting(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4"
      style={{ background: "rgba(14,34,64,0.55)" }}
      onClick={onClose}
      data-testid="att-promote-overlay"
    >
      <div
        className="w-full max-w-xl rounded-md relative"
        style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 flex items-center justify-between gap-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>From the directory</div>
            <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>Bring someone in</h3>
            <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
              Everyone starts in the church directory. Pick them here and one tap adds them to attendance.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid="att-promote-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded" style={{ background: "var(--surface-hover)" }}>
            <Search size={16} style={{ color: "var(--muted)" }} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, or phone…"
              className="flex-1 bg-transparent text-sm outline-none"
              style={{ color: "var(--ink)" }}
              data-testid="att-promote-search"
            />
          </div>

          <div>
            <label className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Add them to section (optional)</label>
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm" data-testid="att-promote-section">
              <option value="">— Unassigned —</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="flex items-center justify-between gap-2">
            <span className="text-xs" style={{ color: "var(--muted)" }}>Not in the directory yet?</span>
            <button onClick={() => setAdding(true)} className="btn-ghost text-xs" data-testid="att-promote-add-new"><UserPlus size={13} /> Add to directory</button>
          </div>
          <div className="space-y-1 max-h-[22rem] overflow-y-auto" data-testid="att-promote-results">
            {loading && <p className="text-sm p-3" style={{ color: "var(--muted)" }}>Searching…</p>}
            {!loading && results.length === 0 && (
              <p className="text-sm p-3" style={{ color: "var(--muted)" }}>
                No one in the directory matches.
              </p>
            )}
            {results.map((p) => {
              const meta = STATUS_META[p.lifecycle_status] || STATUS_META.guest;
              const already = p.section_id;
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-3 p-3 rounded"
                  style={{ borderBottom: "1px solid var(--border-subtle)" }}
                  data-testid={`att-promote-row-${p.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(p)}</span>
                      <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: meta.tint, color: "#FFFFFF" }}>{meta.label}</span>
                      {already && <span className="text-[0.55rem] uppercase tracking-wider" style={{ color: "var(--olive)" }}>· in a section</span>}
                    </div>
                    {(p.phone || p.email) && (
                      <div className="text-[0.7rem] mt-0.5" style={{ color: "var(--muted)" }}>{p.phone || p.email}</div>
                    )}
                  </div>
                  <button
                    onClick={() => promote(p)}
                    disabled={promoting === p.id}
                    className="btn-primary text-xs"
                    data-testid={`att-promote-add-${p.id}`}
                  >
                    {promoting === p.id ? "Adding…" : "Add"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {adding && (
        <div onClick={(e) => e.stopPropagation()}>
          <AddDirectoryPersonDialog
            orgId={orgId}
            defaultLifecycle="member"
            initialName={query}
            onClose={() => setAdding(false)}
            onCreated={(p) => { setAdding(false); promote(p); }}
            onUseExisting={(p) => { setAdding(false); promote(p); }}
          />
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------- SECTIONS ---
function SectionsPanel({ orgId, sections, disciples, leaders, onReload }) {
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [assigning, setAssigning] = useState(null); // section object

  const disciplesInSection = (sid) => disciples.filter((d) => d.section_id === sid);
  const leaderNameForUser = (uid) => leaders.find((l) => l.user_id === uid)?.name || "Unassigned";
  const leaderNameForLeaderId = (lid) => leaders.find((l) => l.id === lid)?.name || "";

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/att/sections/${confirmDelete.id}`, { params: { org_id: orgId } });
      toast.success("Section removed.");
      setConfirmDelete(null);
      onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete."); }
  };

  return (
    <section data-testid="att-sections-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm max-w-xl" style={{ color: "var(--ink-2)" }}>
          Sections split your church into groups. Each section has a leader who marks attendance for the disciples in that group.
        </p>
        <button onClick={() => setModal({ kind: "new" })} className="btn-primary" data-testid="att-add-section-btn"><Plus size={14} /> New section</button>
      </div>
      {sections.length === 0 ? (
        <EmptyState icon={UsersRound} title="No sections yet" description="Create your first section (e.g., 'Adult · North Aisle')." action={<button onClick={() => setModal({ kind: "new" })} className="btn-primary"><Plus size={14} /> New section</button>} testid="att-sections-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sections.map((s) => {
            const inSec = disciplesInSection(s.id);
            return (
              <div key={s.id} className="card-surface p-5" data-testid={`att-section-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <div className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>{s.name}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                      Primary: <span style={{ color: "var(--ink-2)" }}>{leaderNameForLeaderId(s.leader_id) || leaderNameForUser(s.leader_user_id)}</span>
                    </div>
                    {s.alt_leader_id && (
                      <div className="text-xs" style={{ color: "var(--muted)" }}>
                        Alternate: <span style={{ color: "var(--ink-2)" }}>{leaderNameForLeaderId(s.alt_leader_id)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setModal({ kind: "edit", section: s })} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-edit-section-${s.id}`} title="Edit"><Edit3 size={13} style={{ color: "var(--ink-2)" }} /></button>
                    <button onClick={() => setConfirmDelete(s)} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-delete-section-${s.id}`} title="Remove"><Trash2 size={13} style={{ color: "var(--terracotta)" }} /></button>
                  </div>
                </div>
                <div className="text-xs mb-3" style={{ color: "var(--olive)" }}>
                  {inSec.length} disciple{inSec.length === 1 ? "" : "s"} assigned
                </div>
                {inSec.length > 0 && (
                  <div className="text-xs space-y-0.5 mb-3 max-h-24 overflow-y-auto pl-1" style={{ color: "var(--ink-2)" }}>
                    {inSec.slice(0, 5).map((d) => <div key={d.id}>· {formatDisplayName(d)}</div>)}
                    {inSec.length > 5 && <div style={{ color: "var(--muted)" }}>and {inSec.length - 5} more…</div>}
                  </div>
                )}
                <button onClick={() => setAssigning(s)} className="btn-ghost text-xs" data-testid={`att-assign-section-${s.id}`}>
                  <UserPlus size={12} /> Assign disciples <ChevronRight size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {modal && <SectionModal orgId={orgId} section={modal.section} leaders={leaders} onClose={() => setModal(null)} onSaved={onReload} />}
      {confirmDelete && (
        <ConfirmDialog title={`Remove ${confirmDelete.name}?`} message="Disciples in this section will become unassigned. This cannot be undone." onCancel={() => setConfirmDelete(null)} onConfirm={doDelete} confirmLabel="Remove section" />
      )}
      {assigning && <AssignDisciplesModal orgId={orgId} section={assigning} disciples={disciples} onClose={() => setAssigning(null)} onSaved={onReload} />}
    </section>
  );
}

function SectionModal({ orgId, section, leaders, onClose, onSaved }) {
  const isEdit = !!section;
  const [form, setForm] = useState({
    name: section?.name || "",
    leader_id: section?.leader_id || "",
    alt_leader_id: section?.alt_leader_id || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Section name is required."); return; }
    if (form.leader_id && form.leader_id === form.alt_leader_id) { toast.error("Primary and alternate leaders must be different."); return; }
    setBusy(true);
    try {
      const payload = { name: form.name, leader_id: form.leader_id || null, alt_leader_id: form.alt_leader_id || null };
      if (isEdit) await api.put(`/att/sections/${section.id}`, payload, { params: { org_id: orgId } });
      else await api.post("/att/sections", payload, { params: { org_id: orgId } });
      toast.success(isEdit ? "Saved." : "Section created.");
      onSaved(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  const leaderOptions = leaders || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="att-section-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-4" style={{ color: "var(--ink)" }}>{isEdit ? "Edit section" : "New section"}</h2>
        <Field label="Section name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="att-section-name" className="w-full px-3 py-2 text-sm" /></Field>
        <Field label="Primary leader">
          <select value={form.leader_id} onChange={(e) => setForm({ ...form, leader_id: e.target.value })} data-testid="att-section-leader" className="w-full px-3 py-2 text-sm">
            <option value="">— Unassigned —</option>
            {leaderOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Alternate leader (optional)">
          <select value={form.alt_leader_id} onChange={(e) => setForm({ ...form, alt_leader_id: e.target.value })} data-testid="att-section-alt-leader" className="w-full px-3 py-2 text-sm">
            <option value="">— None —</option>
            {leaderOptions.filter((l) => l.id !== form.leader_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <p className="text-[0.65rem] mt-1" style={{ color: "var(--muted)" }}>The alternate can log in and mark attendance on the primary's behalf.</p>
        <div className="mt-6 flex gap-2">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="att-section-submit">{busy ? "Saving…" : (isEdit ? "Save changes" : "Create section")}</button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function AssignDisciplesModal({ orgId, section, disciples, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(disciples.filter((d) => d.section_id === section.id).map((d) => d.id)));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const l = q.trim().toLowerCase();
    return disciples.filter((d) => !l || `${d.first_name} ${d.last_name}`.toLowerCase().includes(l));
  }, [disciples, q]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const currentIds = new Set(disciples.filter((d) => d.section_id === section.id).map((d) => d.id));
      const toAdd = [...selected].filter((id) => !currentIds.has(id));
      const toRemove = [...currentIds].filter((id) => !selected.has(id));
      if (toAdd.length) {
        await api.post(`/att/sections/${section.id}/assign`, { disciple_ids: toAdd }, { params: { org_id: orgId } });
      }
      // Unassign removed disciples by nulling their section_id
      for (const id of toRemove) {
        await api.put(`/att/disciples/${id}`, { section_id: null }, { params: { org_id: orgId } });
      }
      toast.success("Section roster updated.");
      onSaved(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <div className="w-full max-w-lg card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="att-assign-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-1" style={{ color: "var(--ink)" }}>Assign disciples</h2>
        <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>To section: <strong style={{ color: "var(--ink-2)" }}>{section.name}</strong></p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full px-3 py-2 text-sm mb-3" data-testid="att-assign-search" />
        <div className="max-h-80 overflow-y-auto border rounded" style={{ borderColor: "var(--border-subtle)" }}>
          {filtered.length === 0 ? (
            <p className="p-4 text-xs italic" style={{ color: "var(--muted)" }}>No disciples match. Add some from the Disciples tab first.</p>
          ) : filtered.map((d) => {
            const inOther = d.section_id && d.section_id !== section.id;
            return (
              <label key={d.id} className="flex items-center gap-2 px-3 py-2 border-b cursor-pointer hover:bg-[var(--surface-hover)]" style={{ borderColor: "var(--border-subtle)" }}>
                <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} data-testid={`att-assign-check-${d.id}`} />
                <span className="text-sm flex-1" style={{ color: "var(--ink)" }}>{formatDisplayName(d)}</span>
                {inOther && <span className="text-[0.6rem]" style={{ color: "var(--terracotta)" }}>in another section</span>}
              </label>
            );
          })}
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="att-assign-submit">{busy ? "Saving…" : "Save assignments"}</button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- SERVICES ---
function ServicesPanel({ orgId, services, onReload }) {
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showOrgQr, setShowOrgQr] = useState(false);

  const recurLabel = (svc) => {
    if (svc.recurrence.kind === "weekly") return "Every week";
    if (svc.recurrence.kind === "biweekly") return "Every other week";
    if (svc.recurrence.kind === "monthly") {
      const map = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "Last" };
      return `${map[svc.recurrence.nth] || ""} ${DAYS[svc.day_of_week]} of the month`;
    }
    return "—";
  };

  const doDelete = async () => {
    try {
      await api.delete(`/att/services/${confirmDelete.id}`, { params: { org_id: orgId } });
      toast.success("Service removed.");
      setConfirmDelete(null); onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete."); }
  };

  return (
    <section data-testid="att-services-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm max-w-xl" style={{ color: "var(--ink-2)" }}>
          Configure every recurring service you'll track attendance for. Weekly, bi-weekly, or the Nth day of the month.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowOrgQr(true)} className="btn-ghost" data-testid="att-show-org-qr-btn" title="One QR for the whole church — auto-picks the closest service">
            <QrCode size={14} /> Show QR
          </button>
          <button onClick={() => setModal({ kind: "new" })} className="btn-primary" data-testid="att-add-service-btn"><Plus size={14} /> New service</button>
        </div>
      </div>
      {services.length === 0 ? (
        <EmptyState icon={Calendar} title="No services yet" description="Add a service like 'Sunday AM' or 'Wednesday Bible Study' so section leaders can take attendance for it." action={<button onClick={() => setModal({ kind: "new" })} className="btn-primary"><Plus size={14} /> New service</button>} testid="att-services-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {services.map((s) => (
            <div key={s.id} className="card-surface p-5" data-testid={`att-service-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>{s.name}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                    {DAYS[s.day_of_week]} · {s.start_time}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setModal({ kind: "edit", service: s })} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-edit-service-${s.id}`} title="Edit"><Edit3 size={13} style={{ color: "var(--ink-2)" }} /></button>
                  <button onClick={() => setConfirmDelete(s)} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-delete-service-${s.id}`} title="Remove"><Trash2 size={13} style={{ color: "var(--terracotta)" }} /></button>
                </div>
              </div>
              <div className="inline-flex items-center gap-1 text-[0.65rem] px-2 py-0.5 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}>
                <ClipboardCheck size={11} /> {recurLabel(s)}
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && <ServiceModal orgId={orgId} service={modal.service} onClose={() => setModal(null)} onSaved={onReload} />}
      {showOrgQr && <OrgQrModal orgId={orgId} onClose={() => setShowOrgQr(false)} />}
      {confirmDelete && (
        <ConfirmDialog title={`Remove ${confirmDelete.name}?`} message="Section leaders will no longer see this service. Past attendance records are preserved." onCancel={() => setConfirmDelete(null)} onConfirm={doDelete} confirmLabel="Remove service" />
      )}
    </section>
  );
}

function OrgQrModal({ orgId, onClose }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.get("/att/qr-code", { params: { org_id: orgId } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load the QR code."));
  }, [orgId]);
  const publicUrl = data ? `${window.location.origin}/checkin/${data.code}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy."); }
  };
  const print = () => window.print();
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4 print:static print:py-0 print:px-0"
      style={{ background: "rgba(14,34,64,0.55)" }}
      onClick={onClose}
      data-testid="att-org-qr-overlay"
    >
      <style>{`@media print { body * { visibility: hidden; } .qr-print, .qr-print * { visibility: visible; } .qr-print { position: absolute; left: 0; top: 0; width: 100%; } .qr-no-print { display: none !important; } }`}</style>
      <div
        className="w-full max-w-md rounded-md relative qr-print"
        style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start justify-between gap-3 qr-no-print">
            <div>
              <div className="overline mb-1" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Church-wide QR check-in</div>
              <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>{data?.org_name || "…"}</h3>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid="att-org-qr-close" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="mt-6 flex flex-col items-center text-center">
            <div className="p-6 rounded" style={{ background: "#FFFFFF" }} data-testid="att-org-qr-code">
              {publicUrl ? <QRCodeSVG value={publicUrl} size={220} level="M" includeMargin={false} /> : <div style={{ width: 220, height: 220 }} />}
            </div>
            <div className="mt-4 font-serif-display text-xl" style={{ color: "var(--ink)" }}>Check in for today's service</div>
            <div className="mt-1 text-sm max-w-xs" style={{ color: "var(--muted)" }}>
              Scan with your phone camera. We'll mark you present at the service closest to right now (Central time).
            </div>
            <div className="mt-4 text-[0.65rem] px-3 py-1.5 rounded font-mono" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }} data-testid="att-org-qr-url">
              {publicUrl || "…"}
            </div>
          </div>

          <div className="mt-6 flex gap-2 flex-wrap qr-no-print">
            <button onClick={copy} disabled={!publicUrl} className="btn-ghost" data-testid="att-org-qr-copy">
              <Copy size={13} /> {copied ? "Copied" : "Copy link"}
            </button>
            <button onClick={print} disabled={!publicUrl} className="btn-primary" data-testid="att-org-qr-print">
              <Printer size={13} /> Print poster
            </button>
          </div>
          <p className="mt-4 text-[0.65rem] qr-no-print" style={{ color: "var(--muted)" }}>
            One QR for the whole church. Post it around the building. It automatically records attendance against whichever service is closest to check-in time.
          </p>
        </div>
      </div>
    </div>
  );
}

function ServiceModal({ orgId, service, onClose, onSaved }) {
  const isEdit = !!service;
  const [form, setForm] = useState({
    name: service?.name || "",
    day_of_week: service?.day_of_week ?? 0,
    start_time: service?.start_time || "10:00",
    recurrence: {
      kind: service?.recurrence?.kind || "weekly",
      nth: service?.recurrence?.nth || 1,
      anchor_date: service?.recurrence?.anchor_date || new Date().toISOString().slice(0, 10),
    },
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Service name is required."); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        day_of_week: Number(form.day_of_week),
        start_time: form.start_time,
        recurrence: {
          kind: form.recurrence.kind,
          ...(form.recurrence.kind === "monthly" ? { nth: Number(form.recurrence.nth) } : {}),
          ...(form.recurrence.kind === "biweekly" ? { anchor_date: form.recurrence.anchor_date } : {}),
        },
      };
      if (isEdit) await api.put(`/att/services/${service.id}`, payload, { params: { org_id: orgId } });
      else await api.post("/att/services", payload, { params: { org_id: orgId } });
      toast.success(isEdit ? "Saved." : "Service created.");
      onSaved(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="att-service-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-4" style={{ color: "var(--ink)" }}>{isEdit ? "Edit service" : "New service"}</h2>
        <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="att-service-name" placeholder="Sunday AM Worship" className="w-full px-3 py-2 text-sm" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Day of week">
            <select value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: e.target.value })} data-testid="att-service-day" className="w-full px-3 py-2 text-sm">
              {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </Field>
          <Field label="Start time">
            <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="att-service-time" className="w-full px-3 py-2 text-sm" />
          </Field>
        </div>
        <Field label="Recurrence">
          <select value={form.recurrence.kind} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, kind: e.target.value } })} data-testid="att-service-recurrence" className="w-full px-3 py-2 text-sm">
            <option value="weekly">Every week</option>
            <option value="biweekly">Every other week</option>
            <option value="monthly">Nth occurrence per month</option>
          </select>
        </Field>
        {form.recurrence.kind === "monthly" && (
          <Field label="Which occurrence">
            <select value={form.recurrence.nth} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, nth: e.target.value } })} data-testid="att-service-nth" className="w-full px-3 py-2 text-sm">
              <option value={1}>1st {DAYS[form.day_of_week]}</option>
              <option value={2}>2nd {DAYS[form.day_of_week]}</option>
              <option value={3}>3rd {DAYS[form.day_of_week]}</option>
              <option value={4}>4th {DAYS[form.day_of_week]}</option>
              <option value={5}>Last {DAYS[form.day_of_week]}</option>
            </select>
          </Field>
        )}
        {form.recurrence.kind === "biweekly" && (
          <Field label="Starting on">
            <input type="date" value={form.recurrence.anchor_date} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, anchor_date: e.target.value } })} data-testid="att-service-anchor" className="w-full px-3 py-2 text-sm" />
          </Field>
        )}
        <div className="mt-6 flex gap-2">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="att-service-submit">{busy ? "Saving…" : (isEdit ? "Save changes" : "Create service")}</button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// -------------------------------------------------------------- LEADERS ---
function LeadersPanel({ orgId, leaders, sections, onReload }) {
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const sectionName = (id) => sections.find((s) => s.id === id)?.name || "—";

  const doDelete = async () => {
    try {
      await api.delete(`/att/leaders/${confirmDelete.id}`, { params: { org_id: orgId } });
      toast.success("Section leader removed.");
      setConfirmDelete(null); onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete."); }
  };

  return (
    <section data-testid="att-leaders-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm max-w-xl" style={{ color: "var(--ink-2)" }}>
          Each section is looked after by a leader. Set their login credentials — they'll sign in at <code>/attendance</code> and see only their own section's roster.
        </p>
        <button onClick={() => setModal({ kind: "new" })} className="btn-primary" data-testid="att-add-leader-btn"><Plus size={14} /> New section leader</button>
      </div>
      {leaders.length === 0 ? (
        <EmptyState icon={KeySquare} title="No section leaders yet" description="Add your first leader. You'll create their login now; they can change their password anytime." action={<button onClick={() => setModal({ kind: "new" })} className="btn-primary"><Plus size={14} /> New section leader</button>} testid="att-leaders-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {leaders.map((l) => (
            <div key={l.id} className="card-surface p-5" data-testid={`att-leader-card-${l.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-medium" style={{ color: "var(--ink)" }}>{l.name}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{l.email}</div>
                  <div className="text-[0.65rem] uppercase tracking-[0.14em] mt-1" style={{ color: "var(--gold)" }}>Section: {sectionName(l.section_id)}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setModal({ kind: "edit", leader: l })} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-edit-leader-${l.id}`} title="Edit"><Edit3 size={13} style={{ color: "var(--ink-2)" }} /></button>
                  <button onClick={() => setConfirmDelete(l)} className="p-1.5 hover:bg-[var(--surface-hover)] rounded" data-testid={`att-delete-leader-${l.id}`} title="Remove"><Trash2 size={13} style={{ color: "var(--terracotta)" }} /></button>
                </div>
              </div>
              {l.phone && <div className="text-xs" style={{ color: "var(--ink-2)" }}>{l.phone}</div>}
            </div>
          ))}
        </div>
      )}
      {modal && <LeaderModal orgId={orgId} leader={modal.leader} sections={sections} onClose={() => setModal(null)} onSaved={onReload} />}
      {confirmDelete && (
        <ConfirmDialog title={`Remove ${confirmDelete.name}?`} message="They'll lose access to the attendance portal. Their user record stays intact for other roles." onCancel={() => setConfirmDelete(null)} onConfirm={doDelete} confirmLabel="Remove leader" />
      )}
    </section>
  );
}

function LeaderModal({ orgId, leader, sections, onClose, onSaved }) {
  const isEdit = !!leader;
  const [form, setForm] = useState({
    name: leader?.name || "",
    email: leader?.email || "",
    phone: leader?.phone || "",
    section_id: leader?.section_id || "",
    password: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    if (!isEdit && form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, email: form.email, phone: form.phone, section_id: form.section_id || null };
        if (form.password) payload.password = form.password;
        await api.put(`/att/leaders/${leader.id}`, payload, { params: { org_id: orgId } });
      } else {
        await api.post("/att/leaders", { ...form, section_id: form.section_id || null }, { params: { org_id: orgId } });
      }
      toast.success(isEdit ? "Saved." : "Leader created — they can now sign in.");
      onSaved(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center py-10 px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md card-surface p-8 relative" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="att-leader-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
        <h2 className="font-serif-display text-2xl mb-4" style={{ color: "var(--ink)" }}>{isEdit ? "Edit section leader" : "New section leader"}</h2>
        <Field label="Full name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="att-leader-name" className="w-full px-3 py-2 text-sm" /></Field>
        <Field label="Email *"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="att-leader-email" className="w-full px-3 py-2 text-sm" /></Field>
        <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="att-leader-phone" className="w-full px-3 py-2 text-sm" /></Field>
        <Field label="Section">
          <select value={form.section_id} onChange={(e) => setForm({ ...form, section_id: e.target.value })} data-testid="att-leader-section" className="w-full px-3 py-2 text-sm">
            <option value="">— Unassigned —</option>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label={isEdit ? "New password (leave blank to keep)" : "Password *"}>
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="att-leader-password" placeholder={isEdit ? "unchanged" : "min 8 chars"} className="w-full px-3 py-2 text-sm" />
        </Field>
        <div className="mt-6 flex gap-2">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="att-leader-submit">{busy ? "Saving…" : (isEdit ? "Save changes" : "Create leader")}</button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- SHARED ---
function Field({ label, children, span2 }) {
  return (
    <div className={`mb-3 ${span2 ? "col-span-2" : ""}`}>
      <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>{label}</label>
      {children}
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, action, testid }) {
  return (
    <div className="p-10 rounded-md text-center" style={{ background: "var(--bg-alt)", border: "1px dashed var(--border-subtle)" }} data-testid={testid}>
      <Icon size={30} style={{ color: "var(--muted)", margin: "0 auto 0.75rem" }} />
      <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>{title}</h3>
      <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "var(--muted)" }}>{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

function ConfirmDialog({ title, message, onCancel, onConfirm, confirmLabel = "Confirm" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(14,34,64,0.6)" }} onClick={onCancel}>
      <div className="w-full max-w-md card-surface p-8" style={{ background: "var(--bg-alt)" }} onClick={(e) => e.stopPropagation()} data-testid="att-confirm-dialog">
        <h3 className="font-serif-display text-xl mb-2" style={{ color: "var(--ink)" }}>{title}</h3>
        <p className="text-sm mb-5" style={{ color: "var(--ink-2)" }}>{message}</p>
        <div className="flex gap-2">
          <button onClick={onConfirm} className="btn-primary" style={{ background: "var(--terracotta)" }} data-testid="att-confirm-yes">{confirmLabel}</button>
          <button onClick={onCancel} className="btn-ghost" data-testid="att-confirm-no">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- REPORTS ---
function ReportsPanel({ orgId, sections }) {
  const [sectionId, setSectionId] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("name"); // "name" | "section" | "percentage" | "attended"
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    if (!orgId) return;
    setBusy(true);
    const params = { org_id: orgId };
    if (sectionId) params.section_id = sectionId;
    api.get("/att/reports/ytd", { params })
      .then((r) => setReport(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load report."))
      .finally(() => setBusy(false));
  }, [orgId, sectionId]);

  const rows = useMemo(() => {
    const list = report?.rows || [];
    const l = q.trim().toLowerCase();
    const filtered = l
      ? list.filter((r) =>
          `${r.first_name} ${r.last_name}`.toLowerCase().includes(l) ||
          (r.section_name || "").toLowerCase().includes(l)
        )
      : list;
    const sorted = [...filtered].sort((a, b) => {
      let av, bv;
      if (sortKey === "name") { av = `${a.last_name} ${a.first_name}`.toLowerCase(); bv = `${b.last_name} ${b.first_name}`.toLowerCase(); }
      else if (sortKey === "section") { av = (a.section_name || "").toLowerCase(); bv = (b.section_name || "").toLowerCase(); }
      else if (sortKey === "percentage") { av = a.percentage; bv = b.percentage; }
      else if (sortKey === "attended") { av = a.attended; bv = b.attended; }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [report, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "percentage" || key === "attended" ? "desc" : "asc"); }
  };

  const totals = useMemo(() => {
    if (!rows.length) return { avg: 0, tracked: 0 };
    const withData = rows.filter((r) => r.total > 0);
    if (!withData.length) return { avg: 0, tracked: 0 };
    const avg = withData.reduce((s, r) => s + r.percentage, 0) / withData.length;
    return { avg: Math.round(avg * 10) / 10, tracked: withData.length };
  }, [rows]);

  const pctColor = (pct) => {
    if (pct >= 80) return "var(--forest, #2f6b4f)";
    if (pct >= 50) return "var(--gold, #b3811a)";
    return "var(--terracotta)";
  };

  return (
    <section data-testid="att-reports-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)", borderRadius: "999px", padding: "0.4rem 0.9rem" }}>
            <Search size={14} style={{ color: "var(--muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or section…" className="bg-transparent outline-none text-sm w-64" data-testid="att-report-search" />
          </div>
          <div>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Section</label>
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} className="px-3 py-2 text-sm" data-testid="att-report-section-filter">
              <option value="">All sections</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        {report && (
          <div className="text-right">
            <div className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
              Year {report.year} · as of {report.as_of}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
              Avg attendance: <span className="font-semibold" style={{ color: pctColor(totals.avg) }}>{totals.avg}%</span>
              <span className="mx-2" style={{ color: "var(--muted)" }}>·</span>
              <span data-testid="att-report-tracked-count">{totals.tracked} people tracked</span>
            </div>
          </div>
        )}
      </div>

      {busy ? (
        <p style={{ color: "var(--muted)" }}>Loading report…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title={report?.rows?.length ? "No matches" : "No attendance data yet"}
          description={report?.rows?.length ? "Try clearing the search or changing the section filter." : "Once people are added to a section and services are scheduled, YTD attendance percentages will appear here."}
          testid="att-report-empty"
        />
      ) : (
        <div className="card-surface overflow-hidden" data-testid="att-report-table">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--surface-hover)" }}>
                <th className="text-left px-4 py-3 font-medium" style={{ color: "var(--ink-2)" }}>
                  <button onClick={() => toggleSort("name")} className="flex items-center gap-1 hover:opacity-80" data-testid="att-report-sort-name">
                    Name <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: "var(--ink-2)" }}>
                  <button onClick={() => toggleSort("section")} className="flex items-center gap-1 hover:opacity-80" data-testid="att-report-sort-section">
                    Section <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: "var(--ink-2)" }}>
                  <button onClick={() => toggleSort("attended")} className="flex items-center gap-1 hover:opacity-80" data-testid="att-report-sort-attended">
                    Attended <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="text-right px-4 py-3 font-medium" style={{ color: "var(--ink-2)" }}>
                  <button onClick={() => toggleSort("percentage")} className="flex items-center gap-1 hover:opacity-80 ml-auto" data-testid="att-report-sort-percentage">
                    YTD % <ArrowUpDown size={12} />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.guest_id} className="border-t" style={{ borderColor: "var(--border-subtle)" }} data-testid={`att-report-row-${r.guest_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium" style={{ color: "var(--ink)" }}>{formatDisplayName(r, { lastFirst: true })}</div>
                    <div className="text-[0.65rem] uppercase tracking-[0.14em] mt-0.5" style={{ color: "var(--muted)" }}>{r.lifecycle_status} · since {r.since}</div>
                  </td>
                  <td className="px-4 py-3" style={{ color: "var(--ink-2)" }}>{r.section_name || "—"}</td>
                  <td className="px-4 py-3" style={{ color: "var(--ink-2)" }}>{r.attended} / {r.total}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-hover)" }}>
                        <div style={{ width: `${Math.min(100, r.percentage)}%`, height: "100%", background: pctColor(r.percentage) }} />
                      </div>
                      <span className="font-semibold tabular-nums" style={{ color: pctColor(r.percentage), minWidth: "3.5rem", textAlign: "right" }} data-testid={`att-report-pct-${r.guest_id}`}>
                        {r.total === 0 ? "—" : `${r.percentage}%`}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
